/**
 * Semantic colors for feature UI that is shared by every Lemmon theme.
 *
 * Keep these as complete CSS color values (rather than DaisyUI's internal
 * color channels) so they can also be consumed by inline styles and canvas.
 */
const darkFloorPlanColors = {
  "--floor-plan-background": "#1c2128",
  "--floor-plan-grid-line": "rgba(255,255,255,0.06)",
  "--floor-plan-grid-padding": "rgba(0,0,0,0.15)",
  "--floor-plan-section-divider": "rgba(255,255,255,0.10)",
  "--floor-plan-selection-background": "rgba(96,165,250,0.30)",
  "--floor-plan-selection-border": "#3b82f6",
  "--floor-plan-fixture-background": "#3a3445",
  "--floor-plan-fixture-content": "rgba(255,255,255,0.85)",
  "--floor-plan-fixture-shadow":
    "0 2px 8px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.06)",
  "--floor-plan-chair-background": "rgba(90,95,105,0.85)",
  "--floor-plan-chair-border": "rgba(60,65,75,0.75)",
  "--floor-plan-chair-shadow": "0 1px 2px rgba(0,0,0,0.35)",
  "--floor-plan-table-free-background": "#b0b4bc",
  "--floor-plan-table-free-content": "#2a2a30",
  "--floor-plan-table-free-shadow":
    "0 2px 8px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.18)",
  "--floor-plan-table-busy-background": "#4a6ad4",
  "--floor-plan-table-busy-content": "#ffffff",
  "--floor-plan-table-busy-shadow":
    "0 3px 10px rgba(74,106,212,0.33), inset 0 1px 0 rgba(255,255,255,0.10)",
  "--floor-plan-table-reserved-background": "rgba(245,158,11,0.90)",
  "--floor-plan-table-reserved-content": "#000000",
  "--floor-plan-table-reserved-ring": "#fcd34d",
  "--floor-plan-table-reserved-glow": "rgba(251,191,36,0.55)",
  "--floor-plan-table-unavailable-background": "rgba(239,68,68,0.90)",
  "--floor-plan-table-unavailable-content": "#ffffff",
  "--floor-plan-reservation-background": "#ea580c",
  "--floor-plan-reservation-overflow-background": "rgba(234,88,12,0.60)",
  "--floor-plan-reservation-late-background": "#ef4444",
  "--floor-plan-reservation-content": "#ffffff",
  "--floor-plan-empty-content": "#6b7280",
  "--floor-plan-sheet-backdrop": "rgba(0,0,0,0.55)",
};

const lightFloorPlanColors = {
  "--floor-plan-background": "#eef2f7",
  "--floor-plan-grid-line": "rgba(55,65,81,0.14)",
  "--floor-plan-grid-padding": "rgba(55,65,81,0.06)",
  "--floor-plan-section-divider": "rgba(55,65,81,0.16)",
  "--floor-plan-selection-background": "rgba(37,99,235,0.18)",
  "--floor-plan-selection-border": "#2563eb",
  "--floor-plan-fixture-background": "#dce3ec",
  "--floor-plan-fixture-content": "#374151",
  "--floor-plan-fixture-shadow":
    "0 2px 8px rgba(15,23,42,0.14), inset 0 1px 0 rgba(255,255,255,0.75)",
  "--floor-plan-chair-background": "rgba(100,116,139,0.78)",
  "--floor-plan-chair-border": "rgba(71,85,105,0.65)",
  "--floor-plan-chair-shadow": "0 1px 2px rgba(15,23,42,0.20)",
  "--floor-plan-table-free-background": "#cbd5e1",
  "--floor-plan-table-free-content": "#1f2937",
  "--floor-plan-table-free-shadow":
    "0 2px 8px rgba(15,23,42,0.16), inset 0 1px 0 rgba(255,255,255,0.65)",
  "--floor-plan-table-busy-background": "#315fc4",
  "--floor-plan-table-busy-content": "#ffffff",
  "--floor-plan-table-busy-shadow":
    "0 3px 10px rgba(49,95,196,0.28), inset 0 1px 0 rgba(255,255,255,0.18)",
  "--floor-plan-table-reserved-background": "#f59e0b",
  "--floor-plan-table-reserved-content": "#1f2937",
  "--floor-plan-table-reserved-ring": "#b45309",
  "--floor-plan-table-reserved-glow": "rgba(217,119,6,0.38)",
  "--floor-plan-table-unavailable-background": "#dc2626",
  "--floor-plan-table-unavailable-content": "#ffffff",
  "--floor-plan-reservation-background": "#c2410c",
  "--floor-plan-reservation-overflow-background": "rgba(194,65,12,0.72)",
  "--floor-plan-reservation-late-background": "#dc2626",
  "--floor-plan-reservation-content": "#ffffff",
  "--floor-plan-empty-content": "#64748b",
  "--floor-plan-sheet-backdrop": "rgba(15,23,42,0.45)",
};

const darkOrderCardColors = {
  "--order-filter-header-background": "var(--order-card-background)",
  "--order-filter-control-background": "#111827",
  "--order-card-background": "#2b3847",
  "--order-card-border": "#6b7280",
  "--order-card-section-border": "rgba(255,255,255,0.13)",
  "--order-card-muted-content": "#9ca3af",
  "--order-card-quantity-background": "#ffffff",
  "--order-card-quantity-content": "#000000",
  "--order-card-takeaway-background": "#beccdc",
  "--order-card-takeaway-content": "#000000",
  "--order-card-dine-in-background": "#738599",
  "--order-card-dine-in-content": "#ffffff",
  "--order-card-status-completed-background": "#b7e9fd",
  "--order-card-status-completed-content": "#000000",
  "--order-card-status-ready-background": "#1f9947",
  "--order-card-status-ready-content": "#ffffff",
  "--order-card-status-preparing-background": "#ffbb33",
  "--order-card-status-preparing-content": "#000000",
  "--order-card-status-new-background": "#cc295f",
  "--order-card-status-new-content": "#ffffff",
  "--order-card-status-hold-background": "#ca2625",
  "--order-card-status-hold-content": "#ffffff",
};

const lightOrderCardColors = {
  "--order-filter-header-background": "var(--order-card-background)",
  "--order-filter-control-background": "#f4f6f9",
  "--order-card-background": "#ffffff",
  "--order-card-border": "#cbd5e1",
  "--order-card-section-border": "rgba(55,65,81,0.16)",
  "--order-card-muted-content": "#6b7280",
  "--order-card-quantity-background": "#374151",
  "--order-card-quantity-content": "#ffffff",
  "--order-card-takeaway-background": "#dbeafe",
  "--order-card-takeaway-content": "#1e3a5f",
  "--order-card-dine-in-background": "#475569",
  "--order-card-dine-in-content": "#ffffff",
  "--order-card-status-completed-background": "#bae6fd",
  "--order-card-status-completed-content": "#0c4a6e",
  "--order-card-status-ready-background": "#16a34a",
  "--order-card-status-ready-content": "#ffffff",
  "--order-card-status-preparing-background": "#fbbf24",
  "--order-card-status-preparing-content": "#422006",
  "--order-card-status-new-background": "#db2777",
  "--order-card-status-new-content": "#ffffff",
  "--order-card-status-hold-background": "#dc2626",
  "--order-card-status-hold-content": "#ffffff",
};

const walkerDarkColors = {
  "--floor-plan-walker-ink": "rgba(255,255,255,0.93)",
  "--floor-plan-walker-ink-dim": "rgba(255,255,255,0.55)",
  "--floor-plan-walker-shadow": "rgba(0,0,0,0.50)",
  "--floor-plan-walker-accent": "#ffdc64",
  "--floor-plan-walker-accent-alt": "#c896ff",
  "--floor-plan-walker-warm": "#ffc864",
  "--floor-plan-walker-danger": "#c83232",
  "--floor-plan-walker-success": "#64b464",
  "--floor-plan-walker-detail": "#c8b496",
  "--floor-plan-walker-detail-dim": "rgba(180,180,180,0.30)",
  "--floor-plan-walker-band": "rgba(180,180,180,0.85)",
  "--floor-plan-walker-instrument": "#966e50",
  "--floor-plan-walker-portal-core": "#000000",
  "--floor-plan-walker-portal-middle": "#140a28",
  "--floor-plan-walker-portal-edge": "#7850b4",
  "--floor-plan-walker-zombie-low": "80,100,80",
  "--floor-plan-walker-zombie-high": "120,150,110",
};

const walkerLightColors = {
  "--floor-plan-walker-ink": "rgba(31,41,55,0.90)",
  "--floor-plan-walker-ink-dim": "rgba(55,65,81,0.58)",
  "--floor-plan-walker-shadow": "rgba(15,23,42,0.35)",
  "--floor-plan-walker-accent": "#b7791f",
  "--floor-plan-walker-accent-alt": "#7c3aed",
  "--floor-plan-walker-warm": "#c2410c",
  "--floor-plan-walker-danger": "#b91c1c",
  "--floor-plan-walker-success": "#15803d",
  "--floor-plan-walker-detail": "#78634f",
  "--floor-plan-walker-detail-dim": "rgba(71,85,105,0.35)",
  "--floor-plan-walker-band": "rgba(71,85,105,0.82)",
  "--floor-plan-walker-instrument": "#7c4a2d",
  "--floor-plan-walker-portal-core": "#1e1b4b",
  "--floor-plan-walker-portal-middle": "#4c1d95",
  "--floor-plan-walker-portal-edge": "#8b5cf6",
  "--floor-plan-walker-zombie-low": "55,100,65",
  "--floor-plan-walker-zombie-high": "95,145,90",
};

export const darkComponentColors = {
  ...darkFloorPlanColors,
  ...darkOrderCardColors,
  ...walkerDarkColors,
};

export const lightComponentColors = {
  ...lightFloorPlanColors,
  ...lightOrderCardColors,
  ...walkerLightColors,
};
